// GradientHeader — hlavička stránky, jednotná pro celý admin.
//
// 2.9.2026 (fáze 2 redesignu): dřív existovalo šest gradientových variant plus
// osmnáct ručně namalovaných kopií tmavého banneru přímo ve stránkách. Teď je
// gradient jeden. Prop `variant` zůstává kvůli tomu, aby se nemuselo sáhnout do
// stránek, které ji předávají, ale všechny hodnoty renderují totéž.
//
// Akce jsou VŽDYCKY jen ikony, bez textu, na telefonu i na počítači
// (rozhodnutí z fáze 0). Ikona má `aria-label` i `title`, takže na počítači
// vyskočí popisek myší a čtečka ji přečte. Co se nedá vyjádřit ikonou, není
// častá akce a patří do nabídky pod třemi tečkami, ne do hlavičky.
//
// Nadskupina nad nadpisem (`eyebrow`) se nepíše, je vidět v menu; prop zůstává
// kvůli kompatibilitě a ignoruje se. Podnadpis má smysl jen tehdy, když říká
// něco, co na stránce jinde není.
//
// Použití:
//   <GradientHeader title="Automatizace" icon={Zap} subtitle="Stav 16 automatizací">
//     {/* volitelné akce, obvykle přes prop `akce` */}
//   </GradientHeader>
//
//   <GradientHeader title="Výplaty a fakturace" icon={Receipt}
//     akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch }]} />

import React, { useLayoutEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import InfoTip from "./InfoTip.jsx";

// 10.9.2026: ikona stránky i akční tlačítka mají stejnou velikost — na
// telefonu 44 px (dotykové pravidlo), na počítači 40. Dřív to bylo obrácené
// a nic nesedělo na jedné ose.
const VELIKOST_IKONY = "h-11 w-11 sm:h-10 sm:w-10";
const TRIDA_IKONY =
  `flex ${VELIKOST_IKONY} shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 active:bg-white/20 disabled:opacity-40 sm:hover:bg-white/20`;

/** Ikonová tlačítka v hlavičce. Nikdy s textem vedle ikony. */
function Akce({ akce }) {
  return akce.map(({ klic, ikona: Ikona, popis, onClick, disabled, ...zbytek }) => (
    <button
      key={klic || popis}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={popis}
      title={popis}
      className={TRIDA_IKONY}
      {...zbytek}
    >
      <Ikona className="h-[18px] w-[18px]" />
    </button>
  ));
}

export default function GradientHeader({
  eyebrow, // ponecháno kvůli kompatibilitě, nekreslí se
  title,
  subtitle,
  icon: Icon,
  loading = false,
  badge,
  children,
  variant, // ponecháno kvůli kompatibilitě, gradient je jeden
  iconInline = false,
  akce,
  napoveda, // text nápovědy; schová se pod ikonu stránky vlevo
  tecka, // stav v rohu ikony stránky: "ok", "neutralni", "prazdna"
}) {
  const maSeznam = Array.isArray(akce) && akce.length > 0;

  // 10.9.2026: podtitulek se pod nadpisem už nekreslí. Když ho stránka pořád
  // posílá, bere se jako text nápovědy, aby se informace neztratila.
  const textNapovedy = typeof napoveda === "string" && napoveda.trim()
    ? napoveda
    : (typeof subtitle === "string" && subtitle.trim() ? subtitle : "");

  // Vejdou se ikony vedle názvu? Dřív se to hádalo podle jejich počtu, takže
  // krátké „Lidé" mělo zbytečné druhé patro. Teď se to změří — stejný postup,
  // jakým se rozhodují záložky (`Zalozky` v ui.jsx).
  const obal = useRef(null);
  const rada = useRef(null);
  const merenyNazev = useRef(null);
  const [nevejde, setNevejde] = useState(false);
  const klice = `${typeof title === "string" ? title : ""}|${akce?.map((a) => a.klic || a.popis).join(",") || ""}|${textNapovedy ? 1 : 0}`;
  useLayoutEffect(() => {
    const el = obal.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const zmer = () => {
      const kDispozici = el.clientWidth;
      const ikony = rada.current?.offsetWidth || 0;
      // Šířka názvu se měří na neviditelné kopii, která se nikdy nezalomí.
      // Kdyby se četla z viditelného nadpisu, vyšla by po zalomení šířka
      // sloupce, ne to, co název opravdu potřebuje.
      const nazev = merenyNazev.current?.offsetWidth || 0;
      if (!kDispozici || !nazev) return;
      // Ikona stránky je stejně velká jako tlačítka v řadě, takže se vezme
      // rovnou z prvního z nich. Dvě mezery po 12 px jsou z `gap-3`.
      const ikonaStranky = rada.current?.firstElementChild?.offsetWidth || 44;
      const proNazev = kDispozici - ikonaStranky - 12 - ikony - 12;
      setNevejde(nazev > proNazev);
    };
    zmer();
    const ro = new ResizeObserver(zmer);
    ro.observe(el);
    document.fonts?.ready?.then(zmer).catch(() => {});
    return () => ro.disconnect();
  }, [klice]);

  return (
    <div className="rounded-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 p-4 text-white shadow-glass sm:p-5">
      {/* `children` (selecty, přepínače, RefreshButton) si drží chování z doby
          před redesignem: na počítači vpravo v řadě, na telefonu vlastní řada
          pod nadpisem přes celou šířku. Kdyby se mačkaly vedle názvu, popisek
          by se na telefonu lámal po jednom slově. Ikonové `akce` sedí vždycky
          nahoře vedle názvu. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div ref={obal} className="flex min-w-0 flex-1 flex-wrap items-start gap-3">
          {/* Ikona stránky. Když je co vysvětlit, je z ní tlačítko s otazníkem
              v rohu a klepnutím se otevře nápověda. Bez nápovědy zůstává
              obyčejnou značkou stránky jako dosud. */}
          {Icon && !iconInline && (
            textNapovedy ? (
              <InfoTip text={textNapovedy} varianta="ikona" ikona={Icon} tecka={tecka} />
            ) : (
              <span className={`flex ${VELIKOST_IKONY} shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10`}>
                <Icon className="h-5 w-5" />
              </span>
            )
          )}
          <div className="relative min-w-0 flex-1">
            {/* Stejná minimální výška jako ikona, aby první řádek názvu seděl
                přesně na jejím středu (dřív byl o 7,5 px výš). */}
            <div className="flex min-h-[44px] flex-wrap items-center gap-2 font-serif text-xl font-bold leading-tight [overflow-wrap:anywhere] sm:min-h-[40px] sm:text-2xl">
              {Icon && iconInline && <Icon className="h-6 w-6 shrink-0" />}
              <span>{title}</span>
              {loading && <RefreshCw className="h-4 w-4 animate-spin text-white/85" />}
              {badge}
            </div>
            {/* Neviditelná kopie názvu, jen kvůli měření. Nikdy se nezalomí,
                takže její šířka je přesně to, co název potřebuje. */}
            {typeof title === "string" && (
              <span
                ref={merenyNazev}
                aria-hidden
                className="pointer-events-none absolute left-0 top-0 -z-10 whitespace-nowrap font-serif text-xl font-bold leading-tight opacity-0 sm:text-2xl"
              >
                {title}
              </span>
            )}
          </div>
          {maSeznam && (
            <div
              className={`flex shrink-0 items-start gap-2 ${nevejde ? "w-full justify-end" : ""}`}
              data-akce-hlavicky
            >
              <span ref={rada} className="flex shrink-0 items-start gap-2">
                <Akce akce={akce} />
              </span>
            </div>
          )}
        </div>
        {children && (
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:shrink-0 sm:justify-end [&>*]:flex-1 [&>*]:min-w-[7rem] [&>*]:justify-center sm:[&>*]:flex-none sm:[&>*]:min-w-0 sm:[&>*]:justify-start">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
